import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavBarCollapsableContext } from '../../../helpers/context'
import { active, activeWhite, inactive, inactiveWhite } from '../../../images'
import DialogModal from '../../../components/dialogModal'
import EditLinksForm from '../../../components/forms/links/formEdit'
import { useWindowSize } from '../../../helpers/hooks/hookWindowsSize'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import Calendar from '../../../components/calendar'
import FormFilters from '../../../components/forms/links/formFilters'
import { useNavigate } from 'react-router-dom'
import LinkList from '../../../components/linkTable'
import ExportCsv from '../../../components/exportCsv'
import InputTag from '../../../components/inputTag'
import DeleteLinkForm from '../../../components/forms/links/formDelete'

import {
  deleteLink,
  editLink,
  getLinks,
  getTotalLinks,
  postActivateLinks,
  postAddTags,
  postDeactivateLinks,
} from '../../../redux/reducers/urlReducer'
import { AppDispatch, useAppDispatch } from '../../../redux/store'
import { getToken, getTokenExpiration, logOut } from '../../../helpers/utils'
import { toast } from 'react-toastify'

/**Links is a view returns the components LinksCards with the urls created adn the modal for editing and deleting them */

interface LinkValues {
  _id: string
  original_url: string
  short_url: string
  title: string
  domain: string
  custom_code: string
  tags: string[]
  has_qr: boolean
  utm: utmValues
  status: number
  tracking_count: number
  createdAt: string
}

interface utmValues {
  origin: string
  media: string
  campaign: string
  term: string
  content: string
}

interface EditFormValues {
  _id: string
  custom_code: string
  title: string
  short_url: string
  original_url: string
  tags: string[]
  domain: string
  utm: utmValues
}

interface DeleteFormValues {
  _id: string
}

interface FilterValues {
  tag: string
}

interface Dates {
  startDate: Date
  endDate: Date
  key: string
}

const Links = () => {
  const { t } = useTranslation()

  const dispatch: AppDispatch = useAppDispatch()
  let navigate = useNavigate()

  const initialLinkValues: LinkValues = {
    _id: '',
    original_url: '',
    short_url: '',
    title: '',
    domain: '',
    custom_code: '',
    tags: [],
    has_qr: false,
    utm: {
      origin: '',
      media: '',
      campaign: '',
      term: '',
      content: '',
    },
    status: 1,
    createdAt: '',
    tracking_count: 0,
  }

  const currentDate = new Date()
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  )
  const formattedFirstDayOfMonth = `${firstDayOfMonth.getFullYear()}-${(
    firstDayOfMonth.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}-01`
  const formattedDate = `${currentDate.getFullYear()}-${(
    currentDate.getMonth() + 1
  )
    .toString()
    .padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`

  const classContext = useContext(NavBarCollapsableContext)
  const [linkSelected, setLinkSelected] =
    useState<LinkValues>(initialLinkValues)
  const [open, setOpen] = useState<boolean>(false)
  const [openDelete, setOpenDelete] = useState<boolean>(false)
  const [openAddTag, setOpenAddTag] = useState<boolean>(false)
  const [updateList, setUpdateList] = useState<boolean>(false)
  const [openCreatedDate, setOpenCreatedDate] = useState<boolean>(false)
  const [openFilter, setOpenFilter] = useState<boolean>(false)
  const sizeWidth = useWindowSize().width
  const [openEdit, setOpenEdit] = useState<boolean>(false)
  const [showMenuStatus, setShowMenuStatus] = useState<boolean>(false)
  const [isHover, setIsHover] = useState<boolean>(false)
  const [arrayIds, setArrayIds] = useState<string[]>([])
  const [linkData, setLinkData] = useState<LinkValues[]>([])
  const [statusSelected, setStatusSelected] = useState<number>(1)
  const [page, setPage] = useState(1)
  const [totalCountData, setTotalCountData] = useState<number>(0)
  const [totalPagesData, setTotalPagesData] = useState<number>(0)
  const [fromDateSelected, setFromDateSelected] = useState<string>('')
  const [tagData, setTagData] = useState<string[]>([])
  const [toDateSelected, setToDateSelected] = useState<string>('')
  const [tagSelected, setTagSelected] = useState<string[]>([])

  const statusImage =
    statusSelected === 1
      ? isHover
        ? activeWhite
        : active
      : isHover
      ? inactiveWhite
      : inactive

  const statusName = statusSelected === 1 ? 'ACTIVES' : 'INACTIVES'
  const token = getToken()
  const sessionExpired = getTokenExpiration()

  useEffect(() => {
    if (sessionExpired) logOut()
  }, [sessionExpired])

  useEffect(() => {
    getData(page)
    getTotalData()
  }, [tagSelected, statusSelected])

  const handleUpdateList = () => {
    setUpdateList(true)
    getData(page)
    getTotalData()
    setUpdateList(false)
    setOpenCreatedDate(false)
  }

  const getData = async (page: number) => {
      try {
      const { payload } = await dispatch(
        getLinks({
          token: token,
          page: page,
          hasqr: 0,
          status: statusSelected,
          fromDate: fromDateSelected,
          toDate: toDateSelected,
          tag: tagSelected,
        })
      )

      if (payload?.success) {
        setLinkData(payload?.data)
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getTotalData = async () => {
    try {
      const { payload } = await dispatch(
        getTotalLinks({
          token: token,
          hasqr: 0,
          status: statusSelected,
          fromDate: fromDateSelected,
          toDate: toDateSelected,
          tag: tagSelected,
        })
      )

      if (payload?.success) {
        setTotalCountData(payload?.data?.total)
        setTotalPagesData(payload?.data?.pages)
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }
  const handleAddTags = async () => {
    if (!arrayIds) {
      toast(`${t('SELECT_URL')}`)
      return
    }

    if (!tagData) {
      toast(`${t('FILL_TAGS')}`)
      return
    }

    const body = {
      urlIds: arrayIds,
      tags: tagData,
    }
    try {
      const { payload } = await dispatch(
        postAddTags({
          token: token,
          body: body,
        })
      )

      if (payload?.success) {
        toast(`${t('TAGS_ADDED')}`)
        getData(page)
        getTotalData()
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleChangeStatus = async () => {

    if (!arrayIds) {
      toast(`${t('SELECT_URL')}`)
      return
    }

    const body = {
      urlIds: arrayIds,
    }

    const request =
      statusSelected === 1 ? postDeactivateLinks : postActivateLinks
    try {
      const { payload } = await dispatch(
        request({
          token: token,
          body: body,
        })
      )

      if (payload?.success) {
        toast(`${t('STATUS_CHANGED')}`)
        getData(page)
        getTotalData()
      } else {
        console.error(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
    getData(newPage)
  }

  const onChange = (ranges: Dates) => {
    const dateStartDate = ranges.startDate
    const dateStarDateObj = new Date(dateStartDate)
    const yearStartDate = dateStarDateObj.getFullYear()
    const monthStartDay = (dateStarDateObj.getMonth() + 1)
      .toString()
      .padStart(2, '0')
    const dayStartDay = dateStarDateObj.getDate().toString().padStart(2, '0')

    const dateEndDate = ranges.endDate
    const dateEndDateObj = new Date(dateEndDate)
    const yearEndDate = dateEndDateObj.getFullYear()
    const monthEndDay = (dateStarDateObj.getMonth() + 1)
      .toString()
      .padStart(2, '0')
    const dayEndDay = dateEndDateObj.getDate().toString().padStart(2, '0')

    const formattedFromDate = `${yearStartDate}-${monthStartDay}-${dayStartDay}`
    const formattedToDate = `${yearEndDate}-${monthEndDay}-${dayEndDay}`
    setFromDateSelected(formattedFromDate)
    setToDateSelected(formattedToDate)
  }

  const handleTagsChange = (newTagData: string[]) => {
    setTagData(newTagData)
  }

  // Función para actualizar arrayIds desde el hijo
  const getSelectedIds = (newArrayIds: string[]) => {
    setArrayIds(newArrayIds)
  }
  const handleSubmitEdit = (values: EditFormValues) => {
    const body = {
      urlId: values._id,
      url: values.original_url,
      domain: values.domain,
      title: values.title,
      custom_code: values.custom_code,
      has_qr: false,
      utm: {
        origin: values.utm.origin,
        media: values.utm.media,
        campaign: values.utm.campaign,
        term: values.utm.term,
        content: values.utm.content,
      },
      tags: values.tags,
    }

    handleEditLink(body)
    setOpenEdit(false)
  }

  const handleEditLink = async (body: any) => {

    try {
      const { payload } = await dispatch(editLink({ token, body: body }))

      if (payload && payload.success) {
        toast(`${t('DATA_UPDATED')}`)
        getData(page)
        getTotalData()
      } else {
        toast(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleDeleteLink = async (id: string) => {
    try {
      const { payload } = await dispatch(deleteLink({ token, urlId: id }))

      if (payload && payload.success) {
        toast(`${t('DATA_DELETED')}`)
        getData(page)
        getTotalData()
      } else {
        toast(`${t('ERROR')}`)
      }
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const handleSubmitDelete = (values: DeleteFormValues) => {
    handleDeleteLink(values._id)
    setOpenDelete(false)
  }

  const handleOpenShowStatus = () => {
    setShowMenuStatus(!showMenuStatus)
  }

  const handleOpenFilters = () => {
    setOpenFilter(true)
    // setOpenCreatedDate(false)
  }

  const handleOpenCreatedDate = () => {
    setOpenFilter(false)
    setOpenCreatedDate(true)
  }

  const handleSubmitFilters = (values: FilterValues) => {
    setTagSelected([values.tag])
    setOpenFilter(false)
  }

  const getLinkData = (linkCode: LinkValues) => {
    setLinkSelected(linkCode)
  }

  const handleSelectStatus = (selectedStatus: number) => {
    setStatusSelected(selectedStatus)
    setShowMenuStatus(false)
  }

  const handleOpenAddtag = () => {
    setOpenCreatedDate(false)
    setOpen(false)
    setOpenEdit(false)
    setOpen(false)
    setOpenDelete(false)
    setOpenAddTag(true)
  }

  const handleCleanFilters = () => {
    setStatusSelected(1)
    setFromDateSelected('')
    setToDateSelected('')
    setTagSelected([])
  }

  const handleCloseEdit = () => {
    setOpenEdit(false)
  }

  const handleCloseDelete = () => {
    setOpenDelete(false)
  }

  const handleTags = () => {
    handleAddTags()
    setOpenAddTag(false)
  }

  const Menu = () => {
    return (
      <div className='container-option-extra-status'>
        <button
          className='button-option-extra'
          onClick={() => handleSelectStatus(1)}
        >
          <img
            src={active}
            alt={`${t('ACTIVES')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('ACTIVES')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => handleSelectStatus(0)}
        >
          <img
            src={inactive}
            alt={`${t('INACTIVES')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('INACTIVES')}`}</span>
        </button>
      </div>
    )
  }
  return (
    <>
      <div className={`container-home`}>
        <div className={classContext.classes.navbar_padding_left}>
          <div className='container-table'>
            <div className='container-title-table pb-5'>
              <span className='principal-title'>{`${t('LINKS')}`}</span>
              <button
                className={`button-second ${
                  sizeWidth < 768 ? 'margin-top-2 width-100' : 'width-auto'
                }`}
                type='button'
                onClick={() => navigate('/create-new-link')}
              >
                {`${t('CREATE_NEW')}`}
              </button>
            </div>
            <div
              className={`container-buttton-center-filters ${
                sizeWidth < 768 ? '' : 'd-flex'
              }`}
            >
              <button
                className={`button-sixth margin-right-2 ${
                  sizeWidth < 768 ? 'margin-bottom-1 width-100' : 'width-auto'
                }`}
                type='button'
                onClick={handleOpenCreatedDate}
              >
                {`${t('FILTER_BY_DATE')}`}
              </button>
              <button
                className={`button-sixth ${
                  sizeWidth < 768 ? 'width-100' : 'width-auto'
                }`}
                type='submit'
                onClick={handleOpenFilters}
              >
                {`${t('INCLUDE_FILTERS')}`}
              </button>
              <button
                className={`button-transparent ${
                  sizeWidth < 768 ? 'width-100' : 'width-auto'
                }`}
                type='submit'
                onClick={handleCleanFilters}
              >
                {`${t('CLEAN_ALL_FILTERS')}`}
              </button>
              <button
                className={`button-transparent color-primary text-decoration-underline ${
                  sizeWidth < 768 ? 'width-100' : 'width-auto'
                }`}
                type='submit'
                onClick={handleOpenAddtag}
              >
                {`${t('ADD_TAG')}`}
              </button>

              <button
                className={`button-transparent ${
                  sizeWidth < 768 ? 'width-100' : 'width-auto'
                }`}
                type='submit'
                onClick={handleChangeStatus}
              >
                {`${t(statusSelected === 1 ? 'DEACTIVATE' : 'ACTIVATE')}`}
              </button>

              <ExportCsv data={linkData} />

              <div className='margin-left-auto'>
                <button
                  className={`button-sixth ${
                    sizeWidth < 768 ? 'margin-y-1 width-100' : ' width-140'
                  }`}
                  type='button'
                  onClick={handleOpenShowStatus}
                  onMouseOver={() => setIsHover(true)}
                  onMouseOut={() => setIsHover(false)}
                >
                  <img
                    src={statusImage}
                    alt={`${t(statusName)}`}
                    className='icon-option-extra padding-right-1'
                  />
                  {`${t(statusName)}`}
                </button>
                {showMenuStatus && <Menu />}
              </div>
            </div>

            <LinkList
              totalRecords={totalCountData}
              totalPages={totalPagesData}
              dataBody={linkData}
              dataSelected={getLinkData}
              setOpenEdit={setOpenEdit}
              setOpenDelete={setOpenDelete}
              arrayIds={arrayIds}
              getSelectedIds={getSelectedIds}
              setArrayIds={setArrayIds}
              onCounterChange={handleChangePage}
              onPageChange={setPage}
            />
          </div>
        </div>

        <DialogModal
          title={`${t('EDIT_LINK')}`}
          open={openEdit}
          handleClose={() => setOpenEdit(false)}
        >
          <div className='d-flex flex-column'>
            <EditLinksForm
              data={linkSelected}
              onSubmit={handleSubmitEdit}
              type='link'
              handleClose={handleCloseEdit}
            />
          </div>
        </DialogModal>

        <DialogModal
          title={`${t('ADD_TAG')}`}
          open={openAddTag}
          handleClose={() => setOpenAddTag(false)}
          className='dialog-modal-container-custom'
        >
          <div className='p-5'>
            <InputTag
              tags={tagData}
              label={t('TAGS')}
              classNameLabel={'color-primary'}
              onTagDataChange={handleTagsChange}
            />
            <div className='container-buttton-right'>
              <button
                className={`button-third ${
                  sizeWidth > 768 ? 'margin-right-2' : 'margin-bottom-1 mx-2'
                }`}
                type='button'
                onClick={() => {
                  setOpenAddTag(false)
                }}
              >
                {`${t('CANCEL')}`}
              </button>
              <button
                className='button-first'
                type='submit'
                onClick={() => handleTags()}
              >
                {`${t('SAVE')}`}
              </button>
            </div>
          </div>
        </DialogModal>

        <DialogModal
          title={`${t('DELETE_LINK')}`}
          open={openDelete}
          handleClose={() => setOpenDelete(false)}
        >
          <DeleteLinkForm
            data={linkSelected}
            onSubmit={handleSubmitDelete}
            handleClose={handleCloseDelete}
          />
        </DialogModal>

        <DialogModal
          title={`${t('FILTER_BY_DATE')}`}
          open={openCreatedDate}
          handleClose={() => setOpenCreatedDate(false)}
          className='modal-filter-date'
        >
          <div className='d-flex flex-column div-body-padding p-4'>
            <Calendar onChange={onChange} />
            <div className='container-buttton-center'>
              <button
                className={`button-third ${
                  sizeWidth > 768 ? 'margin-right-2' : 'margin-bottom-1'
                }`}
                type='button'
                onClick={() => {
                  setOpenCreatedDate(false)
                }}
              >
                {`${t('CANCEL')}`}
              </button>
              <button
                className='button-first'
                type='submit'
                onClick={() => {
                  handleUpdateList()
                }}
              >
                {`${t('APPLY')}`}
              </button>
            </div>
          </div>
        </DialogModal>

        <DialogModal
          title={`${t('ADD_FILTERS')}`}
          open={openFilter}
          handleClose={() => setOpenFilter(false)}
        >
          <div className='d-flex flex-column div-body-padding px-5 pb-5'>
            <FormFilters onSubmit={handleSubmitFilters}>
              <div className='container-buttton-center'>
                <button
                  className={`button-third ${
                    sizeWidth > 768 ? 'margin-right-2' : 'margin-bottom-1'
                  }`}
                  type='button'
                  onClick={() => {
                    setOpenFilter(false)
                  }}
                >
                  {`${t('CANCEL')}`}
                </button>
                <button className='button-first' type='submit'>
                  {`${t('APPLY')}`}
                </button>
              </div>
            </FormFilters>
          </div>
        </DialogModal>
      </div>
    </>
  )
}

export default Links
